import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatDateTimeID } from "@/lib/format";
import { ShieldCheck, KeyRound, LogOut } from "lucide-react";

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [twoFA, setTwoFA] = useState(!!user?.two_factor_enabled);

  const submit = async () => {
    if (newPw.length < 8) return toast.error("Kata sandi minimal terdiri dari 8 karakter.");
    if (newPw !== confirmPw) return toast.error("Konfirmasi kata sandi tidak sesuai.");
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Kata sandi berhasil diperbarui.");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Profil Saya</h1>
        <p className="text-slate-500 mt-2">Informasi pribadi dan keamanan akun Anda.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="font-display">Informasi Pribadi</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-full bg-slate-900 text-white grid place-items-center text-lg font-semibold">
                {(user?.full_name || "?").split(" ").map((s) => s[0]).slice(0,2).join("").toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-base">{user?.full_name}</div>
                <div className="text-xs text-slate-500">{user?.position || "-"}</div>
              </div>
            </div>
            <div className="pt-2 space-y-2 text-sm">
              <Row label="Email" value={user?.email} />
              <Row label="Nama Pengguna" value={user?.username} />
              <Row label="Peran" value={<Badge variant="outline">{user?.role_name}</Badge>} />
              <Row label="Departemen" value={user?.department || "-"} />
              <Row label="Nomor Telepon" value={user?.phone || "-"} />
              <Row label="Terakhir Masuk" value={formatDateTimeID(user?.last_login_at)} />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-display flex items-center gap-2"><KeyRound className="h-4 w-4" /> Keamanan Akun</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-3">
              <div className="text-sm font-semibold">Ubah Kata Sandi</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Kata Sandi Saat Ini</Label><Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} data-testid="current-password" /></div>
                <div className="space-y-1.5"><Label>Kata Sandi Baru</Label><Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password" /></div>
                <div className="space-y-1.5"><Label>Konfirmasi Kata Sandi</Label><Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="confirm-password" /></div>
              </div>
              <div className="flex justify-end">
                <Button onClick={submit} disabled={user?.is_demo} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="change-password-button">
                  Simpan Kata Sandi Baru
                </Button>
              </div>
              {user?.is_demo && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">Akun demo tidak dapat mengubah kata sandi.</div>}
            </div>

            <div className="border-t border-slate-200 pt-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Autentikasi Dua Faktor (2FA)</div>
                  <div className="text-xs text-slate-500 mt-1">Tambahkan lapisan keamanan tambahan untuk akun Anda.</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{twoFA ? "Aktif" : "Nonaktif"}</span>
                  <Switch checked={twoFA} onCheckedChange={(v) => { setTwoFA(v); toast.info("Konfigurasi 2FA akan tersedia pada tahap berikutnya."); }} data-testid="toggle-2fa" />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-5">
              <Button variant="outline" onClick={logout} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="logout-all-button">
                <LogOut className="h-4 w-4 mr-2" /> Keluar dari Sesi Ini
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
